import React, { useEffect, useState } from 'react';
import {
    TextField,
    MenuItem,
    Grid,
    FormControlLabel,
    Switch,
    InputAdornment,
    CircularProgress,
    Snackbar,
    Alert,
} from '@mui/material';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { ArrowBack } from '@mui/icons-material';
import { AppButton } from '../../../shared/components/AppButton';
import { getCouponById, createCoupon, updateCoupon } from '../../../shared/services/couponService';
import { CouponAttributes } from '../../../shared/types/coupon';
import moment from 'moment';

interface CouponFormState extends Omit<CouponAttributes, 'id' | 'createdAt' | 'updatedAt' | 'value' | 'minOrderAmount' | 'maxDiscount' | 'usageLimit'> {
    value: number | string;
    minOrderAmount: number | string;
    maxDiscount: number | string;
    usageLimit: number | string;
}

export const AddEditCoupon: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const isEdit = Boolean(id);

    const [formData, setFormData] = useState<CouponFormState>({
        code: '',
        description: '',
        type: 'percentage',
        value: 0,
        minOrderAmount: 0,
        maxDiscount: 0,
        usageLimit: 0,
        startDate: moment().format('YYYY-MM-DD'),
        endDate: moment().add(1, 'month').format('YYYY-MM-DD'),
        isActive: true
    });

    const [loading, setLoading] = useState(false);
    const [fetching, setFetching] = useState(false);
    const [alert, setAlert] = useState({ show: false, type: 'success' as 'success' | 'error', message: '' });
    const [errors, setErrors] = useState<{ [key: string]: string }>({});

    useEffect(() => {
        if (isEdit && id) {
            const fetchCoupon = async () => {
                try {
                    setFetching(true);
                    const response = await getCouponById(id);
                    if (response.success && response.data) {
                        const data = (response.data as any).data || (response.data as any).coupon || response.data;
                        setFormData({
                            code: data.code || '',
                            description: data.description || '',
                            type: data.type || 'percentage',
                            value: data.value || 0,
                            minOrderAmount: data.minOrderAmount || 0,
                            maxDiscount: data.maxDiscount || 0,
                            usageLimit: data.usageLimit || 0,
                            startDate: data.startDate ? moment(data.startDate).format('YYYY-MM-DD') : moment().format('YYYY-MM-DD'),
                            endDate: data.endDate ? moment(data.endDate).format('YYYY-MM-DD') : moment().add(1, 'month').format('YYYY-MM-DD'),
                            isActive: data.isActive ?? true
                        });
                    }
                } catch (error: any) {
                    console.error('Error fetching coupon:', error);
                    setAlert({ show: true, type: 'error', message: 'Error loading coupon details' });
                } finally {
                    setFetching(false);
                }
            };
            fetchCoupon();
        }
    }, [id, isEdit]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value, type, checked } = e.target;

        let finalValue: any = value;
        if (type === 'checkbox') {
            finalValue = checked;
        } else if (type === 'number') {
            // Prevent negative values
            if (value !== '' && Number(value) < 0) {
                return;
            }
            finalValue = value === '' ? '' : Number(value);
        }

        setFormData(prev => ({
            ...prev,
            [name]: finalValue
        }));

        if (errors[name]) {
            setErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[name];
                return newErrors;
            });
        }
    };

    const handleFocus = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        if (value === '0') {
            setFormData(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    const validate = (): boolean => {
        const newErrors: { [key: string]: string } = {};

        if (!formData.code || formData.code.trim() === '') {
            newErrors.code = 'Coupon code is required.';
        }
        if (formData.value === undefined || formData.value === null || formData.value === '') {
            newErrors.value = 'Value is required.';
        }
        if (!formData.startDate) {
            newErrors.startDate = 'Start date is required.';
        }
        if (!formData.endDate) {
            newErrors.endDate = 'End date is required.';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!validate()) return;

        try {
            setLoading(true);

            // Convert empty strings back to 0 or null before sending to API
            const payload = {
                ...formData,
                value: formData.value === '' ? 0 : Number(formData.value),
                minOrderAmount: formData.minOrderAmount === '' ? 0 : Number(formData.minOrderAmount),
                maxDiscount: formData.maxDiscount === '' ? 0 : Number(formData.maxDiscount),
                usageLimit: formData.usageLimit === '' ? 0 : Number(formData.usageLimit),
            };

            if (isEdit && id) {
                await updateCoupon(id, payload);
                setAlert({ show: true, type: 'success', message: 'Coupon updated successfully' });
            } else {
                await createCoupon(payload);
                setAlert({ show: true, type: 'success', message: 'Coupon created successfully' });
            }
            setTimeout(() => {
                navigate('/coupons');
            }, 1000);
        } catch (error: any) {
            console.error('Error saving coupon:', error);
            setAlert({
                show: true,
                type: 'error',
                message: error.response?.data?.message || 'Failed to save coupon'
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="add-edit-admin-container p-[40px]">
            <div className="add-edit-admin-content">
                <div className="form-wrapper max-w-6xl">
                    <div className="-ml-4">
                        <Link to="/coupons" className="back-link">
                            <ArrowBack className="text-[#0EA5E9]" />
                            <span className="ml-2 text-[#0EA5E9]">Back</span>
                        </Link>
                    </div>
                    <div className="title text-2xl sm:text-5xl">
                        {isEdit ? 'Edit Coupon' : 'Add Coupon'}
                    </div>

                    <div className="form-card">
                        {fetching ? (
                            <div className="flex flex-col items-center justify-center p-12">
                                <CircularProgress size={50} />
                                <p className="mt-4 text-gray-500">Loading coupon details...</p>
                            </div>
                        ) : (
                            <>
                                {alert.show && (
                                    <Alert
                                        severity={alert.type}
                                        sx={{ mb: 4, borderRadius: '12px' }}
                                        onClose={() => setAlert({ ...alert, show: false })}
                                    >
                                        {alert.message}
                                    </Alert>
                                )}

                                <form onSubmit={handleSubmit} className="form space-y-4">
                                    <div className="mb-6">
                                        <div className="text-2xl font-bold tracking-tight text-gray-900">
                                            Coupon Information
                                        </div>
                                    </div>

                                    <Grid container spacing={3}>
                                        {/* Code */}
                                        <Grid item xs={12} md={6}>
                                            <TextField
                                                label="Coupon Code"
                                                name="code"
                                                value={formData.code}
                                                onChange={handleChange}
                                                fullWidth
                                                required
                                                inputProps={{ style: { textTransform: 'uppercase' } }}
                                                placeholder="E.g. SAVE20"
                                                error={!!errors.code}
                                                helperText={errors.code}
                                            />
                                        </Grid>

                                        {/* Type */}
                                        <Grid item xs={12} md={6}>
                                            <TextField
                                                select
                                                label="Discount Type"
                                                name="type"
                                                value={formData.type}
                                                onChange={handleChange}
                                                fullWidth
                                                required
                                            >
                                                <MenuItem value="percentage">Percentage (%)</MenuItem>
                                                <MenuItem value="fixed">Fixed Amount (₹)</MenuItem>
                                            </TextField>
                                        </Grid>

                                        {/* Value */}
                                        <Grid item xs={12} md={6}>
                                            <TextField
                                                label="Value"
                                                name="value"
                                                type="number"
                                                value={formData.value}
                                                onChange={handleChange}
                                                onFocus={handleFocus}
                                                fullWidth
                                                required
                                                error={!!errors.value}
                                                helperText={errors.value}
                                                InputProps={{
                                                    endAdornment: <InputAdornment position="end">{formData.type === 'percentage' ? '%' : '₹'}</InputAdornment>,
                                                }}
                                                onWheel={(e) => (e.target as HTMLInputElement).blur()}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
                                                        e.preventDefault();
                                                    }
                                                }}
                                                inputProps={{ min: 0 }}
                                            />
                                        </Grid>

                                        {/* Minimum Order */}
                                        <Grid item xs={12} md={6}>
                                            <TextField
                                                label="Minimum Order Amount"
                                                name="minOrderAmount"
                                                type="number"
                                                value={formData.minOrderAmount}
                                                onChange={handleChange}
                                                onFocus={handleFocus}
                                                fullWidth
                                                InputProps={{
                                                    startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                                                }}
                                                onWheel={(e) => (e.target as HTMLInputElement).blur()}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
                                                        e.preventDefault();
                                                    }
                                                }}
                                                inputProps={{ min: 0 }}
                                            />
                                        </Grid>

                                        {/* Maximum Discount */}
                                        <Grid item xs={12} md={6}>
                                            <TextField
                                                label="Maximum Discount"
                                                name="maxDiscount"
                                                type="number"
                                                value={formData.maxDiscount}
                                                onChange={handleChange}
                                                onFocus={handleFocus}
                                                fullWidth
                                                disabled={formData.type === 'fixed'}
                                                InputProps={{
                                                    startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                                                }}
                                                onWheel={(e) => (e.target as HTMLInputElement).blur()}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
                                                        e.preventDefault();
                                                    }
                                                }}
                                                inputProps={{ min: 0 }}
                                            />
                                        </Grid>

                                        {/* Usage Limit */}
                                        <Grid item xs={12} md={6}>
                                            <TextField
                                                label="Usage Limit"
                                                name="usageLimit"
                                                type="number"
                                                value={formData.usageLimit}
                                                onChange={handleChange}
                                                onFocus={handleFocus}
                                                fullWidth
                                                helperText="0 for unlimited usage"
                                                onWheel={(e) => (e.target as HTMLInputElement).blur()}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
                                                        e.preventDefault();
                                                    }
                                                }}
                                                inputProps={{ min: 0 }}
                                            />
                                        </Grid>

                                        {/* Start Date */}
                                        <Grid item xs={12} md={6}>
                                            <TextField
                                                label="Start Date"
                                                name="startDate"
                                                type="date"
                                                value={formData.startDate}
                                                onChange={handleChange}
                                                fullWidth
                                                required
                                                InputLabelProps={{ shrink: true }}
                                                error={!!errors.startDate}
                                                helperText={errors.startDate}
                                            />
                                        </Grid>

                                        {/* End Date */}
                                        <Grid item xs={12} md={6}>
                                            <TextField
                                                label="End Date"
                                                name="endDate"
                                                type="date"
                                                value={formData.endDate}
                                                onChange={handleChange}
                                                fullWidth
                                                required
                                                InputLabelProps={{ shrink: true }}
                                                error={!!errors.endDate}
                                                helperText={errors.endDate}
                                            />
                                        </Grid>

                                        {/* Description */}
                                        <Grid item xs={12}>
                                            <TextField
                                                label="Description"
                                                name="description"
                                                value={formData.description}
                                                onChange={handleChange}
                                                fullWidth
                                                multiline
                                                rows={3}
                                                placeholder="Enter details about this coupon..."
                                            />
                                        </Grid>

                                        {/* Active Switch */}
                                        <Grid item xs={12}>
                                            <FormControlLabel
                                                control={
                                                    <Switch
                                                        name="isActive"
                                                        checked={formData.isActive}
                                                        onChange={handleChange}
                                                        color="primary"
                                                    />
                                                }
                                                label="Is Active"
                                            />
                                        </Grid>
                                    </Grid>

                                    {/* Action Buttons */}
                                    <div className="form-actions mt-8">
                                        <AppButton
                                            type="button"
                                            variant="cancel"
                                            onClick={() => navigate('/coupons')}
                                            sx={{
                                                minWidth: '120px',
                                                backgroundColor: '#8E8E93',
                                                borderColor: '#8E8E93',
                                                color: '#fff',
                                                '&:hover': { backgroundColor: '#7a7a7f', borderColor: '#7a7a7f' }
                                            }}
                                        >
                                            Cancel
                                        </AppButton>

                                        <AppButton
                                            type="submit"
                                            variant="primary"
                                            disabled={loading}
                                            loading={loading}
                                            sx={{ minWidth: '120px' }}
                                        >
                                            {isEdit ? 'Update Coupon' : 'Create Coupon'}
                                        </AppButton>
                                    </div>
                                </form>
                            </>
                        )}
                    </div>
                </div>
            </div>

            {/* Snackbar */}
            <Snackbar
                open={alert.show}
                autoHideDuration={4000}
                onClose={() => setAlert({ ...alert, show: false })}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert severity={alert.type} variant="filled">
                    {alert.message}
                </Alert>
            </Snackbar>
        </div>
    );
};
